import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Alert,
  Radio,
  Button,
  Tag,
  Spin,
  Typography,
  DatePicker,
} from "antd";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ReloadOutlined,
  RiseOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

const { Title, Text } = Typography;

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function getScoreTag(score) {
  if (score >= 80) return <Tag color="success">สูง</Tag>;
  if (score >= 60) return <Tag color="warning">ปานกลาง</Tag>;
  return <Tag color="error">ต่ำ</Tag>;
}

function getScoreIcon(score) {
  if (score >= 80) return "🟢";
  if (score >= 60) return "🟡";
  return "🔴";
}

const ExecutiveDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePreset, setActivePreset] = useState(7);
  const [dateRange, setDateRange] = useState([dayjs().subtract(6, "day"), dayjs()]);
  const [error, setError] = useState(null);

  const allowedRoles = ["super_admin", "multi_company_admin"];
  const hasAccess = allowedRoles.includes(user?.role);

  const fetchData = useCallback(async () => {
    if (!hasAccess) return;
    setLoading(true);
    setError(null);
    try {
      const params = dateRange
        ? { start_date: dateRange[0].format("YYYY-MM-DD"), end_date: dateRange[1].format("YYYY-MM-DD") }
        : { days: 7 };
      const response = await api.get("/superadmin/executive-dashboard", { params });
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError("ไม่สามารถโหลดข้อมูลได้");
      }
    } catch (err) {
      setError(
        err.response?.data?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"
      );
    } finally {
      setLoading(false);
    }
  }, [dateRange, hasAccess]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!hasAccess) {
    return (
      <Alert
        type="error"
        message="ไม่มีสิทธิ์เข้าถึง"
        description="คุณไม่มีสิทธิ์เข้าถึงหน้านี้ กรุณาติดต่อผู้ดูแลระบบ"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  // Compute heatmap max
  const heatmapData = data?.heatmap || [];
  const maxCount = heatmapData.reduce((m, r) => Math.max(m, r.count), 1);

  // Build heatmap lookup: { "day|hour": count }
  const heatmapLookup = {};
  heatmapData.forEach((r) => {
    heatmapLookup[`${r.day}|${r.hour}`] = r.count;
  });

  // Unique days for heatmap rows
  const heatmapDays = [...new Set(heatmapData.map((r) => r.day))].sort();

  // Compute daily scan rate for LineChart
  const lineChartData = (data?.daily_trend || []).map((r) => ({
    date: r.date,
    "อัตราสแกน (%)":
      r.total > 0 ? Math.round((r.scanned / r.total) * 1000) / 10 : 0,
  }));

  // Company rankings columns
  const rankingsColumns = [
    {
      title: "ลำดับ",
      key: "rank",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "บริษัท",
      dataIndex: "company_name",
      key: "company_name",
      ellipsis: true,
    },
    {
      title: "กำหนดสแกน",
      dataIndex: "total_schedules",
      key: "total_schedules",
      align: "center",
    },
    {
      title: "สแกนแล้ว",
      key: "scanned",
      align: "center",
      render: (_, r) => (
        <Text style={{ color: "#52c41a" }}>
          {(r.completed || 0) + (r.late || 0) + (r.unable || 0)}
        </Text>
      ),
    },
    {
      title: "ตรงเวลา",
      dataIndex: "completed",
      key: "completed",
      align: "center",
      render: (v) => <Text style={{ color: "#52c41a" }}>{v}</Text>,
    },
    {
      title: "ล่าช้า",
      dataIndex: "late",
      key: "late",
      align: "center",
      render: (v) => <Text style={{ color: "#faad14" }}>{v}</Text>,
    },
    {
      title: "พลาด",
      dataIndex: "missed",
      key: "missed",
      align: "center",
      render: (v) => <Text style={{ color: "#ff4d4f" }}>{v}</Text>,
    },
    {
      title: "Score %",
      dataIndex: "score",
      key: "score",
      align: "center",
      render: (v) => (
        <span>
          {getScoreIcon(v)} <strong>{v}%</strong>
        </span>
      ),
      sorter: (a, b) => a.score - b.score,
      defaultSortOrder: "descend",
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      render: (_, record) => getScoreTag(record.score),
    },
  ];

  // Top missed checkpoints columns
  const missedColumns = [
    {
      title: "ชื่อจุดตรวจ",
      dataIndex: "checkpoint_name",
      key: "checkpoint_name",
      ellipsis: true,
    },
    {
      title: "บริษัท",
      dataIndex: "company_name",
      key: "company_name",
      ellipsis: true,
    },
    {
      title: "จำนวนครั้งที่พลาด",
      dataIndex: "missed_count",
      key: "missed_count",
      align: "center",
      render: (v) => <Tag color="error">{v} ครั้ง</Tag>,
    },
  ];

  return (
    <div style={{ padding: "0 0 40px 0" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Executive Dashboard
        </Title>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <Radio.Group
            value={activePreset}
            onChange={(e) => {
              const p = e.target.value;
              setActivePreset(p);
              const today = dayjs();
              const ranges = {
                today:      [today, today],
                yesterday:  [today.subtract(1, "day"), today.subtract(1, "day")],
                this_month: [today.startOf("month"), today],
                last_month: [today.subtract(1, "month").startOf("month"), today.subtract(1, "month").endOf("month")],
                7:          [today.subtract(6, "day"), today],
                30:         [today.subtract(29, "day"), today],
              };
              setDateRange(ranges[p]);
            }}
            buttonStyle="solid"
          >
            <Radio.Button value="today">วันนี้</Radio.Button>
            <Radio.Button value="yesterday">เมื่อวาน</Radio.Button>
            <Radio.Button value="this_month">เดือนนี้</Radio.Button>
            <Radio.Button value="last_month">เดือนที่แล้ว</Radio.Button>
            <Radio.Button value={7}>7 วัน</Radio.Button>
            <Radio.Button value={30}>30 วัน</Radio.Button>
          </Radio.Group>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => {
              setDateRange(dates || [dayjs().subtract(6, "day"), dayjs()]);
              setActivePreset(null);
            }}
            disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
            allowClear={false}
            placeholder={["วันเริ่มต้น", "วันสิ้นสุด"]}
            format="DD/MM/YYYY"
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            รีเฟรช
          </Button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
          closable
        />
      )}

      <Spin spinning={loading}>
        {/* ─── Section 1: KPI Cards ───────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }} align="stretch">
          {[
            {
              title: "รอบที่กำลังดำเนินการ",
              value: data?.kpi?.active_rounds_now ?? "-",
              suffix: "รอบ",
              icon: <RiseOutlined />,
              accent: "#1890ff",
              iconBg: "#e6f7ff",
              sub: null,
            },
            {
              title: "รอบที่มีปัญหา",
              value: data?.kpi?.problem_rounds_now ?? "-",
              suffix: "รอบ",
              icon: <WarningOutlined />,
              accent: "#ff4d4f",
              iconBg: "#fff1f0",
              sub: null,
            },
            {
              title: "อัตราสแกนสำเร็จวันนี้",
              value: data?.kpi?.scan_rate_today ?? "-",
              suffix: "%",
              precision: 1,
              icon: <CheckCircleOutlined />,
              accent: "#52c41a",
              iconBg: "#f6ffed",
              sub: data?.kpi
                ? `${data.kpi.scanned_today} / ${data.kpi.expected_today} จุด${data.kpi.expected_today === 0 ? " (ยังไม่มีรอบที่จบ)" : ""}`
                : null,
            },
            {
              title: "บริษัทที่มีปัญหา",
              value: data?.kpi?.companies_with_issues ?? "-",
              suffix: "บริษัท",
              icon: <BankOutlined />,
              accent: "#fa8c16",
              iconBg: "#fff7e6",
              sub: null,
            },
          ].map((kpi, i) => (
            <Col key={i} xs={24} sm={12} lg={6} style={{ display: "flex" }}>
              <Card
                bordered={false}
                style={{
                  flex: 1,
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  borderTop: `3px solid ${kpi.accent}`,
                  overflow: "hidden",
                }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: "#8c8c8c", marginBottom: 8, fontWeight: 500 }}>
                      {kpi.title}
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      <span style={{ fontSize: 32, fontWeight: 700, color: kpi.accent, lineHeight: 1.2 }}>
                        {kpi.value}
                      </span>
                      <span style={{ fontSize: 14, color: "#595959", fontWeight: 500 }}>
                        {kpi.suffix}
                      </span>
                    </div>
                    <div style={{ minHeight: 20, marginTop: 6 }}>
                      {kpi.sub && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {kpi.sub}
                        </Text>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      width: 52,
                      height: 52,
                      borderRadius: 12,
                      background: kpi.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 22,
                      color: kpi.accent,
                      flexShrink: 0,
                      marginLeft: 12,
                    }}
                  >
                    {kpi.icon}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ─── Section 2: Charts ──────────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} lg={12}>
            <Card
              title="แนวโน้มอัตราสแกน (%)"
              bordered={false}
              bodyStyle={{ padding: "12px 0 0 0" }}
            >
              {lineChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart
                    data={lineChartData}
                    margin={{ top: 5, right: 24, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(d) => d?.slice(5)}
                    />
                    <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`${v}%`, "อัตราสแกน"]} />
                    <Line
                      type="monotone"
                      dataKey="อัตราสแกน (%)"
                      stroke="#1890ff"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{ textAlign: "center", padding: 40, color: "#bbb" }}
                >
                  ไม่มีข้อมูล
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title="สแกนสำเร็จ / ล่าช้า / พลาด รายวัน"
              bordered={false}
              bodyStyle={{ padding: "12px 0 0 0" }}
            >
              {(data?.daily_trend || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={data.daily_trend}
                    margin={{ top: 5, right: 24, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(d) => d?.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="scanned"
                      name="สแกนสำเร็จ"
                      stackId="a"
                      fill="#52c41a"
                    />
                    <Bar
                      dataKey="late"
                      name="ล่าช้า"
                      stackId="a"
                      fill="#faad14"
                    />
                    <Bar
                      dataKey="missed"
                      name="พลาด"
                      stackId="a"
                      fill="#ff4d4f"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{ textAlign: "center", padding: 40, color: "#bbb" }}
                >
                  ไม่มีข้อมูล
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* ─── Section 3: Company Rankings ────────────────────────────────── */}
        <Card
          title="อันดับประสิทธิภาพบริษัท (วันนี้)"
          bordered={false}
          style={{ marginBottom: 20 }}
        >
          <Table
            dataSource={data?.company_rankings || []}
            columns={rankingsColumns}
            rowKey="company_id"
            size="small"
            pagination={false}
            locale={{ emptyText: "ไม่มีข้อมูล" }}
          />
        </Card>

        {/* ─── Section 4: Heatmap ─────────────────────────────────────────── */}
        <Card
          title={`Heatmap การสแกน (${dateRange ? `${dateRange[0].format("DD/MM")} – ${dateRange[1].format("DD/MM")}` : ""} — แกนนอน = ชั่วโมง, แกนตั้ง = วันที่)`}
          bordered={false}
          style={{ marginBottom: 20 }}
        >
          {heatmapDays.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              {/* Hour header */}
              <div
                style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
              >
                <div style={{ width: 90, flexShrink: 0 }} />
                {HOURS.map((h) => (
                  <div
                    key={h}
                    style={{
                      width: 28,
                      flexShrink: 0,
                      textAlign: "center",
                      fontSize: 10,
                      color: "#888",
                    }}
                  >
                    {h}
                  </div>
                ))}
              </div>
              {heatmapDays.map((day) => (
                <div
                  key={day}
                  style={{ display: "flex", alignItems: "center", marginBottom: 3 }}
                >
                  <div
                    style={{
                      width: 90,
                      flexShrink: 0,
                      fontSize: 11,
                      color: "#555",
                      paddingRight: 6,
                      textAlign: "right",
                    }}
                  >
                    {day?.slice(5)}
                  </div>
                  {HOURS.map((h) => {
                    const count = heatmapLookup[`${day}|${h}`] || 0;
                    const opacity = count > 0 ? 0.15 + (count / maxCount) * 0.85 : 0;
                    return (
                      <div
                        key={h}
                        title={`${day} ${String(h).padStart(2, "0")}:00 — ${count} ครั้ง`}
                        style={{
                          width: 26,
                          height: 20,
                          flexShrink: 0,
                          margin: "0 1px",
                          borderRadius: 3,
                          background:
                            count > 0
                              ? `rgba(24, 144, 255, ${opacity})`
                              : "#f0f0f0",
                          cursor: count > 0 ? "pointer" : "default",
                        }}
                      />
                    );
                  })}
                </div>
              ))}
              {/* Legend */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginTop: 8,
                  gap: 6,
                }}
              >
                <Text type="secondary" style={{ fontSize: 11 }}>
                  น้อย
                </Text>
                {[0.15, 0.35, 0.55, 0.75, 1].map((op) => (
                  <div
                    key={op}
                    style={{
                      width: 20,
                      height: 14,
                      borderRadius: 3,
                      background: `rgba(24, 144, 255, ${op})`,
                    }}
                  />
                ))}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  มาก
                </Text>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: 32, color: "#bbb" }}>
              ไม่มีข้อมูล
            </div>
          )}
        </Card>

        {/* ─── Section 5 & 6: Bottom Row ──────────────────────────────────── */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card
              title="จุดตรวจที่พลาดบ่อยที่สุด (Top 5)"
              bordered={false}
            >
              <Table
                dataSource={data?.top_missed_checkpoints || []}
                columns={missedColumns}
                rowKey={(r) => `${r.checkpoint_name}|${r.company_name}`}
                size="small"
                pagination={false}
                locale={{ emptyText: "ไม่มีข้อมูล" }}
              />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="สรุปคำขอหยุดเตือน" bordered={false}>
              <Row gutter={[12, 12]}>
                <Col span={12}>
                  <Card
                    bordered
                    style={{
                      background: "#fff7e6",
                      textAlign: "center",
                      borderRadius: 8,
                    }}
                    bodyStyle={{ padding: "16px 8px" }}
                  >
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: "#fa8c16",
                      }}
                    >
                      {data?.silence_summary?.pending ?? "-"}
                    </div>
                    <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
                      รออนุมัติ
                    </div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card
                    bordered
                    style={{
                      background: "#f6ffed",
                      textAlign: "center",
                      borderRadius: 8,
                    }}
                    bodyStyle={{ padding: "16px 8px" }}
                  >
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: "#52c41a",
                      }}
                    >
                      {data?.silence_summary?.approved_this_month ?? "-"}
                    </div>
                    <div style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
                      อนุมัติเดือนนี้
                    </div>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default ExecutiveDashboard;
